// #region imports

// --- Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

// --- RxJs
import { Observable, of } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

// --- 3rd Party
import { TranslateService } from '@ngx-translate/core';
import { retryBackoff } from 'backoff-rxjs';
import moment from 'moment';
import { isUndefined } from 'lodash';

// --- HS Code
import { environment } from '../../../../environments/environment';
import {
  ApplicationError,
  Constants,
  ErrorMessage,
  NotificationTypes,
  SortDirections,
  SurveyIdentifiersModel,
  SurveyModel,
  SurveySummaryModel,
} from '../../../models';
import { AppService } from '../app.service';
import { ParticipantGroupsBaseService } from './participant-groups-base.service';
import { RespondentsBaseService } from './respondents-base.service';
import { ResponseBaseService } from './response-base.service';
import { ResultsBaseService } from '../results/results-base.service';
import { SurveyBaseService } from './survey-base.service';
import { UserNotificationBaseService } from '../notifications/user-notification-base.service';
import { OidcService } from '../account/oidc.service';

// #endregion

@Injectable({
  providedIn: 'root',
})
export class SurveyService extends SurveyBaseService {
  private retryCounter: number = 0;
  private readonly _MAX_RETRIES: number = 1;
  private isGettingMostRecent: boolean = false;

  constructor(
    private oidcService: OidcService,
    private translate: TranslateService,
    private http: HttpClient,
    participantGroupsService: ParticipantGroupsBaseService,
    resultsService: ResultsBaseService,
    appService: AppService,
    responseService: ResponseBaseService,
    notificationService: UserNotificationBaseService,
    respondentsService: RespondentsBaseService
  ) {
    super(
      participantGroupsService,
      appService,
      responseService,
      resultsService,
      notificationService,
      respondentsService
    );

    this.notificationService.notifications$.subscribe((notifications) => {
      if (!notifications) {
        return;
      }

      const survey = this._selectedSelfSurvey$.value;
      const surveys = this._selfSurveys$.value;
      notifications.forEach((element) => {
        switch (element.type) {
          case NotificationTypes.reportDownloadReady:
            if (
              survey &&
              element.identifier === this._selectedSelfSurvey$?.value?.id
            ) {
              survey.hasReport = true;
              survey.updated = new Date();
              this._selectedSelfSurvey$.next(survey);
              this._hasReport$.next(true);
            }

            if (surveys) {
              surveys
                .filter((s) => s.id === element.identifier)
                .forEach((sv) => {
                  sv.hasReport = true;
                });

              this._selfSurveys$.next(surveys);
            }

            break;
          default:
            console.warn('unexpected Notification Type: ' + element.type);
        }
      });
    });

    this.oidcService.loginChanged$().subscribe((isLoggedIn) => {
      if (!isLoggedIn) {
        localStorage.removeItem(
          Constants.PARTICIPANT_SELECTED_SURVEY
        );
        this._selfSurveys$.next(null);
        this._selectedSelfSurvey$.next(null);
        this._activeSurvey$.next(null);
        this._surveyError$.next(null);
        this._mostRecentSurvey = null;
        this._currentSurveyIdentifiers$.next(null);
      }
    });
  }

  loadSummaries(
    pageSize: number,
    pageNumber: number,
    filter: string,
    sortItem: string,
    sortDirection: SortDirections,
    forceRefresh: boolean = false
  ): void {
    if (forceRefresh || !this._selfSurveys$.value || this._selfSurveys$.value.length === 0) {
      const url =
        environment.apiConfig.surveyApiUrl +
        environment.apiConfig.surveysApi +
        '?' +
        `pageSize=${pageSize}&pageNumber=${pageNumber}&filter=${filter}&sortItem=${sortItem}&sortDirection=${sortDirection}`;

      this.appService.setLoading(true);

      this.http
        .get<SurveySummaryModel[]>(url)
        .pipe(
          retryBackoff(environment.retryConfig),
          catchError(this._summariesError.bind(this))
        )
        .subscribe((result: SurveySummaryModel[] | null) => {
          if (!result) return;

          const currentDate = moment().toDate();
          result.forEach((r) => {
            r.isLocked = moment.utc(r.lockDate).toDate() < currentDate;
          });

          this._selfSurveys$.next(result);
          if (result && result.length > 0) {
            let savedSelectedSurveyId = localStorage.getItem(
              Constants.PARTICIPANT_SELECTED_SURVEY
            );
            const currentSelection = this._selectedSelfSurvey$.value;
            if (currentSelection) {
              savedSelectedSurveyId =
                savedSelectedSurveyId || currentSelection.id;
            }
            if (
              !savedSelectedSurveyId ||
              !result.find(
                (s: SurveySummaryModel) =>
                  s.id.toLowerCase() === savedSelectedSurveyId?.toLowerCase()
              )
            ) {
              this._updateSelectedSurvey(result[0]);
            } else {
              const refreshedCurrent = result.find(
                (s: SurveySummaryModel) =>
                  s.id.toLowerCase() === savedSelectedSurveyId?.toLowerCase()
              );
              if (refreshedCurrent) {
                this._updateSelectedSurvey(refreshedCurrent);
              }
            }
          }
          this.appService.setLoading(false);
        });
    } else {
      if (this._selectedSelfSurvey$.value) {
        this.responseService.loadResponse(this._selectedSelfSurvey$.value.id);
      }
    }
  }

  loadSurveyById(id: string): void {
    const url =
      environment.apiConfig.surveyApiUrl +
      environment.apiConfig.surveysApi +
      `/${id}`;
    this.appService.setLoading(true);

    this.http
      .get<SurveyModel>(url)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._modelError.bind(this))
      )
      .subscribe((result) => {
        this.appService.setLoading(false);
        if (result) {
          this._mostRecentSurvey = {
            surveyId: result?.id,
            participantId: result?.id,
            surveyReference: result?.moniker
          } as SurveyIdentifiersModel;
          this._currentSurveyIdentifiers$.next(this._mostRecentSurvey);
        }
        this._activeSurvey$.next(result);
      });
  }

  override mostRecentParticipantSurvey(): Observable<SurveyIdentifiersModel | null> {
    return this.oidcService.isParticipant$.pipe(
      mergeMap((isParticipant: boolean) => {
        if (isParticipant) {
          if (!isUndefined(this._mostRecentSurvey)) return of(this._mostRecentSurvey);

          this.getMostRecentParticipantSurvey();

          return this.currentSurveyIdentifiers$;
        } else {
          return of(null);
        }
      })
    );
  }

  override getMostRecentParticipantSurvey(ignoreSavedSelectedSurvey: boolean = false): void {
    if (!isUndefined(this._mostRecentSurvey) || this.isGettingMostRecent) return;

    this.isGettingMostRecent = true;

    const savedSelectedSurveyId = localStorage.getItem(
      Constants.PARTICIPANT_SELECTED_SURVEY
    );

    let url =
      environment.apiConfig.surveyApiUrl +
      environment.apiConfig.mostRecentApi;

    if (savedSelectedSurveyId && !ignoreSavedSelectedSurvey) {
      url += `?id=${savedSelectedSurveyId}`;
    }

    this.appService.setLoading(true);

    this.http
      .get<SurveyIdentifiersModel>(url)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._mostRecentError.bind(this))
      )
      .subscribe((result) => {
        this.appService.setLoading(false);
        this.isGettingMostRecent = false;
        if (!result && savedSelectedSurveyId && (this.retryCounter < this._MAX_RETRIES)) {
          this.retryCounter++;
          this.getMostRecentParticipantSurvey(true);
          return;
        }
        this.retryCounter = 0;

        if (result) {
          this._mostRecentSurvey = result;
          this._currentSurveyIdentifiers$.next(result);
          return;
        }

        this._mostRecentSurvey = null;
        this._currentSurveyIdentifiers$.next(null);
      });
  }

  override getSurveySummary(id: string): void {
    if (!id) return;

    if (!!this._selfSurveys$.value) {
      let summary = this._selfSurveys$.value.find(s => s.id.toLowerCase() === id.toLowerCase());
      if (!summary) {
        summary = this._selfSurveys$.value.find(s => s.moniker.toLowerCase() === id.toLowerCase());
      }
      if (summary) {
        this._selectedSelfSurvey$.next(summary);
        return;
      }
    }

    const url =
    environment.apiConfig.surveyApiUrl +
    environment.apiConfig.surveysApi +
    '?' +
    `pageSize=${1}&pageNumber=${1}&filter=${id}`;

    this.appService.setLoading(true);

    this.http
    .get<SurveySummaryModel[]>(url)
    .pipe(
      retryBackoff(environment.retryConfig),
      catchError(this._summariesError.bind(this))
    )
    .subscribe((result: SurveySummaryModel[] | null) => {
      this.appService.setLoading(false);

      if (!result) return;

      const currentDate = moment().toDate();
      result.forEach((r) => {
        r.isLocked = moment.utc(r.lockDate).toDate() < currentDate;
      });

      if (result.length > 0) {
        this._updateSelectedSurvey(result[0]);
        localStorage.setItem(
          Constants.PARTICIPANT_SELECTED_SURVEY, result[0].id
        );
      }
    });
  }

  private _updateSelectedSurvey(model: SurveySummaryModel) {
    model.updated = new Date();
    this._selectedSelfSurvey$.next(model);
    this._hasReport$.next(model.hasReport);
    this._isLocked$.next(model.isLocked);
    this.resultsService.loadSurveyResults(model.moniker);
    this.responseService.loadResponse(model && model.moniker);
    if (model.numberOfRespondents > 0) {
      this.participantGroupsService.loadGroups(model.id);
      this.respondentsService.getPreviousRespondents();
    }
  }

  private _summariesError(err: any): Observable<SurveySummaryModel[] | null> {
    this._handleError(err);
    this._selfSurveys$.next(null);
    return of(null);
  }

  private _modelError(err: any): Observable<SurveyModel | null> {
    this._handleError(err);
    this._isAdding$.next(false);
    return of(null);
  }

  private _mostRecentError(err: any): Observable<SurveyIdentifiersModel | null> {

    if ((err as HttpErrorResponse).status !== undefined) {
      if (err.status === 404) {
        localStorage.removeItem(
          Constants.PARTICIPANT_SELECTED_SURVEY
        );
      } else {
        this._handleError(err);
      }
    }

    return of(null);
  }

  private _handleError(err: any) {
    this.appService.setLoading(false);

    const error: ApplicationError = ApplicationError.create(
      ErrorMessage.genericErrorCode,
      'SurveyService',
      true,
      err
    );

    this.appService.setLastError(error);
  }
}
